import { default as Modal } from 'antd/es/modal';
import { default as Skeleton } from 'antd/es/skeleton';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useOpenNotification } from 'utils/antd/notification-toasts';

import { getVirtualBankStatementsStatementId } from 'api';

import { useOrganisationId } from 'hooks/context';
import { useAsyncMemo } from 'hooks/useAsyncMemo';
import { useQueryParam } from 'hooks/useQueryParam';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { DownloadThinIcon } from '@atoms/Icon/DownloadThinIcon';
import { Spinner } from '@atoms/Spinner/Spinner';

import {
  DOWNLOAD_CSV_BUTTON_TEST_ID,
  EXPORTED_STATEMENT_MODAL_TEST_ID
} from '../constants';

type Props = {
  handleDownload: (id: number) => Promise<unknown>;
};

export const DownloadExportedStatementModal = ({ handleDownload }: Props) => {
  const organisationId = useOrganisationId<number>();
  const openNotification = useOpenNotification();

  const id = useQueryParam('download_statement');

  const [open, setOpen] = useState<boolean>(id !== '');
  const [pending, setPending] = useState<boolean>(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => clearTimeout(timeoutRef.current);
  }, []);

  const reportInfo = useAsyncMemo(async () => {
    if (!id) return undefined;
    try {
      const retval = await getVirtualBankStatementsStatementId(
        organisationId,
        Number(id)
      );
      return retval.data;
    } catch (_) {
      openNotification({
        kind: 'error',
        message: 'Could not fetch exported statement',
        key: 'fetch-exported-statement-info'
      });
      setOpen(false);
    }
  }, [organisationId, id]);

  const onHandleDownloadClick = useCallback(() => {
    if (!reportInfo?.csv_file_id) return;
    setPending((_) => true);
    handleDownload(reportInfo.csv_file_id).finally(() => {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        setOpen((_) => false);
        setPending((_) => false);
      }, 1000);
    });
  }, [handleDownload, reportInfo]);

  return (
    <Modal
      data-testid={EXPORTED_STATEMENT_MODAL_TEST_ID}
      title={
        <div className="mb-6 flex flex-row">
          <DownloadThinIcon />
          <div className="pl-4">Download statement</div>
        </div>
      }
      open={open}
      onCancel={() => setOpen(false)}
      footer={
        <PrimaryButton
          className="mt-4 w-full"
          data-testid={DOWNLOAD_CSV_BUTTON_TEST_ID}
          disabled={!Boolean(reportInfo) || pending}
          onClick={onHandleDownloadClick}
        >
          {pending ? <Spinner /> : 'Download CSV report'}
        </PrimaryButton>
      }
      style={{ top: '25%' }}
      width={336}
    >
      {reportInfo ? (
        <div className="flex flex-col space-y-2 bg-surface-muted p-3">
          <div>
            <div>Date range:</div>
            <div className="font-semibold">
              From {reportInfo.from} To {reportInfo.to}
            </div>
          </div>
          <div>
            <div>Account:</div>
            <div className="font-semibold">
              {reportInfo.virtual_account_number || 'All accounts'}
            </div>
          </div>
          <div>
            <div>Status:</div>
            <div className="font-semibold capitalize">{reportInfo.kind}</div>
          </div>
        </div>
      ) : (
        <Skeleton />
      )}
    </Modal>
  );
};
